import { useMemo, useState } from "react";

import { Box, Collapse } from "@mui/material";

import { ingredientsObjectSelector } from "../../app/commonSelector";
import { InsideIngredient, Substep } from "../../app/recipeCreation/createRecipe.types";
import { useAppSelector } from "../../app/store/hooks";
import { getIngredientsData } from "../../utils/experience2/ingredientsUtil";
import { BlueDot, DownArrow, MoreInfoCollapse, MoreInfoExpand, PlayIcon } from "../../utils/iconRefs/recipeStepsIcons";
import { logEvents } from "../../utils/logging";
import UnitDisplay from "../ingredientsPage/unitDisplay";
import RecipeStepMedia from "../mediaPlayer/RecipeStepMedia";
import IngredientPrep from "../prep/ingredientPrep";
import LocationUI from "../recipeCreation/recipeSteps/locationUI";
import { SubStepTypeEnum } from "../recipeCreation/utils/constants";
import { TextPrimary, TextPrimaryBold, TextSmallBold } from "../typography";
import { isImgUrl } from "./utils/imgUrl";

const InsideIngredients = ({ stepId, stepIndex, substep, recipeId, userCreatedRecipe, substepIndex }: { stepId: string | null, stepIndex: number, substep: InsideIngredient, recipeId: string | undefined, userCreatedRecipe: boolean, substepIndex: number }) => {
    const { name, parameters, type, id, subStepUId } = substep
    const { location } = parameters
    const [ingredientOpenId, setIngredientPrepOpenId] = useState<string | null>('');
    const [isMediaOpen, setIsMediaOpen] = useState<boolean>(true);
    const user = useAppSelector((state) => state.main.user);
    const device = useAppSelector((state) => state.main.device);
    const ingredientsObject = useAppSelector(ingredientsObjectSelector)
    const displayAllIngredients = useMemo(() => substep?.parameters?.items?.map(
        (ing: any) => substep.type === SubStepTypeEnum.INGREDIENTS ? getIngredientsData(ing, ingredientsObject) : {})
        , [substep, ingredientsObject]);

    const initialIngredients = displayAllIngredients?.slice(0, 4);
    const remainingIngredients = displayAllIngredients?.slice(initialIngredients?.length);
    const isImg = isImgUrl(substep.media.url);
    const ingredientsMoreThanDefault = displayAllIngredients?.length > initialIngredients?.length;
    const remainingIngredientsLength = displayAllIngredients?.length - initialIngredients?.length;
    const [showMoreIngredientOption, setShowMoreIngredientOption] = useState<boolean>(ingredientsMoreThanDefault);
    const [isMoreIngredientClicked, setIsMoreIngredientClicked] = useState<boolean>(false);
    const [initialIngredientsView, setInitialIngredientsView] = useState<boolean>(true);
    const isVideoAvailable = substep.media.url && !isImg;
    const substepDataV2: Substep = { ...substep, Recipe_Presets: substep?.Recipe_Presets ?? [], SubSteps_Ingredients_E3s: substep?.SubSteps_Ingredients_E3s ?? [] };

    const handleIngredientClick = (ingredientId: string) => {
        setIngredientPrepOpenId(ingredientId);
        logEvents("null", "APP_PREP_OPENED", {
            userId: user?.id,
            deviceId: device?.displayName ?? device?._id,
            RecipeId: recipeId
        });
    }

    const viewMedia = () => {
        setIsMediaOpen(!isMediaOpen);
        setInitialIngredientsView(true);
        if (isMoreIngredientClicked) setIsMoreIngredientClicked(false);
        if (!showMoreIngredientOption && ingredientsMoreThanDefault) setShowMoreIngredientOption(true);
    }

    const showMoreIngredients = () => {
        setIsMoreIngredientClicked(true);
        setShowMoreIngredientOption(false);
        setIsMediaOpen(false);
        setInitialIngredientsView(false);
    }

    return (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', md: 'space-around' } }}>
            <Box sx={{ padding: '10px 10px 10px 8px' }}>
                <BlueDot />
            </Box>
            <Box sx={{ width: '90%', boxShadow: '0px 1px 4px 0px #00000040', borderRadius: '10px', overflow: 'hidden' }}>
                <LocationUI
                    location={location}
                    name={name}
                    stepId={stepId}
                    stepIndex={stepIndex}
                    substep={substepDataV2}
                    substepId={id}
                    substepIndex={substepIndex}
                    subStepUId={subStepUId}
                    type={type}
                    userCreatedRecipe={userCreatedRecipe} />
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', padding: isVideoAvailable ? '0 20px 20px 20px' : '20px', boxSizing: 'border-box' }}>
                    {isVideoAvailable &&
                        <>
                            <Box onClick={viewMedia} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', height: '40px' }}>
                                {isMediaOpen ? <MoreInfoCollapse /> : <MoreInfoExpand />}
                            </Box>
                            <Collapse
                                in={isMediaOpen}
                                timeout='auto'
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <RecipeStepMedia
                                        duration={substep.media.duration}
                                        loop={substep.media.loop}
                                        muted={!isMediaOpen || substep.media.muted}
                                        startTime={substep.media.startTime}
                                        url={substep.media.url}
                                    />
                                </Box>
                            </Collapse>
                        </>
                    }

                    {initialIngredients?.map((ingredient) =>
                        <Box key={ingredient.id}>
                            <Box onClick={() => handleIngredientClick(ingredient?.id)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: location === 'Bowl' ? '#D4C6FF' : '#FFDD7C', padding: '10px', borderRadius: '6px', minHeight: '50px' }}>
                                <Box sx={{ textAlign: 'left' }}>
                                    <TextPrimaryBold text={ingredient?.displayName} />
                                    {ingredient?.prep && <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666' }}>{!!ingredient?.prep?.howMedia && <PlayIcon />}<TextSmallBold text={ingredient?.prep?.displayName} /></Box>}
                                </Box>
                                <Box sx={{ textAlign: 'right', width: "40%" }}>
                                    <UnitDisplay ingredient={ingredient} weighScaleAmount={ingredient?.weighScaleAmount} />
                                </Box>
                            </Box>
                            <IngredientPrep ingredient={ingredient} onClose={() => setIngredientPrepOpenId(null)} open={(ingredientOpenId === ingredient?.id)} />
                        </Box>
                    )}
                    <Collapse
                        in={isMoreIngredientClicked}
                        timeout='auto'
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {!initialIngredientsView && isMoreIngredientClicked && remainingIngredients?.map((ingredient) =>
                                <Box key={ingredient?.id}>
                                    <Box onClick={() => handleIngredientClick(ingredient?.id)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: location === 'Bowl' ? '#D4C6FF' : '#FFDD7C', padding: '10px', borderRadius: '6px', minHeight: '50px' }}>
                                        <Box sx={{ textAlign: 'left' }}>
                                            <TextPrimaryBold text={ingredient?.displayName} />
                                            {ingredient?.prep && <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#666' }}>{!!ingredient?.prep?.howMedia && <PlayIcon />}<TextSmallBold text={ingredient?.prep?.displayName} /></Box>}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', width: "40%" }}>
                                            <UnitDisplay ingredient={ingredient} weighScaleAmount={ingredient?.weighScaleAmount} />
                                        </Box>
                                    </Box>
                                    <IngredientPrep ingredient={ingredient} onClose={() => { setIngredientPrepOpenId(null) }} open={(ingredientOpenId === ingredient?.id)} />
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                    {initialIngredientsView && showMoreIngredientOption &&
                        <Box onClick={showMoreIngredients} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <TextPrimary color="#9A9A9A" text={`+ ${remainingIngredientsLength} more ingredient`} />
                            <Box><DownArrow /></Box>
                        </Box>
                    }
                </Box>
            </Box>
        </Box >

    )
}

export default InsideIngredients;